import { useState, useEffect, useCallback, useRef } from 'react';
import { ShieldAlert, Loader2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import legalDocsApi from '@/api/legalDocuments';
import { Button } from '@/components/ui/button';
import LegalDocumentView from '@/components/legal/LegalDocumentView';

const TYPE_LABEL = {
    terms_of_use: 'Termos de Uso',
    privacy_policy: 'Política de Privacidade',
    cookie_policy: 'Política de Cookies',
};

function formatCnpj(value) {
    const d = String(value || '').replace(/\D/g, '');
    if (d.length !== 14) return value || '';
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/**
 * TermsAcceptanceGate — portão de aceite obrigatório, APENAS para administradores.
 *
 * Usuários não-admin nunca são bloqueados nem veem nada (o componente não faz
 * fetch para eles). Para admins, se houver documento vigente ainda não aceito
 * PELA CONTA, exibe um overlay bloqueante com o documento (rolar até o fim) e o
 * aceite. O CNPJ é derivado da conta automaticamente. O aceite vale para a conta
 * inteira — outros admins não verão o modal.
 */
export default function TermsAcceptanceGate() {
    const { isAdmin, isLoading } = useAuth();
    const admin = !isLoading && isAdmin?.();

    const [pending, setPending] = useState([]); // itens do gate ainda não aceitos
    const [account, setAccount] = useState(null); // parte contratante (nome/cnpj)
    const [doc, setDoc] = useState(null); // documento vigente do item atual (com contentMd)
    const [scrolledToEnd, setScrolledToEnd] = useState(false);
    const [accepting, setAccepting] = useState(false);
    const scrollRef = useRef(null);

    const current = pending[0] || null;

    const loadGate = useCallback(async () => {
        try {
            const { pending: pend, account: acc } = await legalDocsApi.getGateStatus();
            setPending(pend || []);
            setAccount(acc || null);
        } catch {
            setPending([]);
        }
    }, []);

    useEffect(() => {
        if (admin) loadGate();
    }, [admin, loadGate]);

    useEffect(() => {
        if (!current) {
            setDoc(null);
            return;
        }
        let cancelled = false;
        setScrolledToEnd(false);
        (async () => {
            try {
                const { document } = await legalDocsApi.getPublished(current.type);
                if (!cancelled) setDoc(document);
            } catch {
                if (!cancelled) setDoc(null);
            }
        })();
        return () => { cancelled = true; };
    }, [current]);

    /**
     * Libera o aceite quando o documento não tem o que rolar.
     *
     * Medir isso no callback de ref não funciona: ele roda uma única vez, antes
     * do flex resolver a altura do container. Nesse instante `clientHeight` é 0
     * enquanto `scrollHeight` já reflete o texto, a comparação dá falso e um
     * documento de uma linha trava o botão para sempre — não há rolagem para
     * destravá-lo depois.
     *
     * O efeito mede após o layout e o ResizeObserver cobre o resto: conteúdo que
     * chega tarde, fonte que carrega, janela que muda de tamanho.
     */
    useEffect(() => {
        const el = scrollRef.current;
        if (!el || !doc) return undefined;

        const measure = () => {
            if (el.scrollHeight - el.clientHeight <= 8) setScrolledToEnd(true);
        };

        measure();

        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [doc]);

    const onScroll = (e) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) setScrolledToEnd(true);
    };

    const handleAccept = async () => {
        if (!current || !doc) return;
        setAccepting(true);
        try {
            // CNPJ é derivado da conta automaticamente no backend.
            await legalDocsApi.acceptTerms({ termsVersion: doc.version, termsType: current.type });
            toast.success('Documento aceito');
            setPending((prev) => prev.slice(1));
        } catch (err) {
            toast.error(err.message || 'Falha ao registrar o aceite');
        } finally {
            setAccepting(false);
        }
    };

    if (!admin || !current || !doc) return null;

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="bg-surface border border-border-strong rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
                <header className="px-6 py-4 border-b border-border">
                    <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-xs font-semibold uppercase tracking-wide">
                        <ShieldAlert className="w-4 h-4" /> Aceite obrigatório
                    </div>
                    <h2 className="text-lg font-semibold text-foreground-strong mt-1">
                        {TYPE_LABEL[current.type] || doc.title}{' '}
                        <span className="text-sm font-normal text-foreground-muted">{doc.version}</span>
                    </h2>
                    {account && (
                        <div className="text-xs text-foreground-muted mt-1.5 flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5" />
                            {account.companyName}
                            {account.cnpj ? ` · CNPJ ${formatCnpj(account.cnpj)}` : ''}
                        </div>
                    )}
                    <p className="text-xs text-foreground-muted mt-1">
                        Atualizamos este documento. Para continuar, leia até o fim e registre o aceite.
                        O aceite vale para toda a conta.
                    </p>
                </header>

                <div
                    ref={scrollRef}
                    onScroll={onScroll}
                    className="flex-1 overflow-y-auto px-6 py-5"
                >
                    <LegalDocumentView content={doc.contentMd} />
                </div>

                <footer className="px-6 py-4 border-t border-border flex items-center justify-between gap-3">
                    <span className="text-xs text-foreground-muted">
                        {scrolledToEnd ? 'Documento lido.' : 'Role o documento até o fim para habilitar o aceite.'}
                    </span>
                    <Button
                        onClick={handleAccept}
                        disabled={!scrolledToEnd || accepting}
                        className="gap-2"
                    >
                        {accepting && <Loader2 className="w-4 h-4 animate-spin" />}
                        Li e aceito
                    </Button>
                </footer>
            </div>
        </div>
    );
}
